<template>
  <v-dialog
    :value="dialog"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    max-width="1880"
    content-class="document-workspace-dialog"
    overlay-color="#111112"
    :overlay-opacity="0.78"
    :transition="$vuetify.breakpoint.xsOnly ? 'dialog-bottom-transition' : 'dialog-transition'"
    @input="onDialogInput">
    <v-card dark class="document-workspace d-flex flex-column fill-height">
      <v-toolbar dark flat class="workspace-toolbar flex-grow-0">
        <v-btn icon class="touch-target close-action mr-1" aria-label="Dokument schließen" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <div class="document-mark" aria-hidden="true">
          <v-icon size="21">mdi-file-document-outline</v-icon>
        </div>

        <div class="workspace-heading">
          <div class="workspace-eyebrow">
            Dokumentvorschau
            <span class="eyebrow-divider">•</span>
            {{ formatDate(document.date) }}
          </div>
          <v-toolbar-title class="workspace-title">
            {{ document.title || "Dokument ohne Titel" }}
          </v-toolbar-title>
        </div>

        <v-spacer></v-spacer>

        <v-chip v-if="$vuetify.breakpoint.mdAndUp" small outlined :color="statusColor(document)" class="status-chip mr-3">
          <v-icon left small>{{ statusIcon(document) }}</v-icon>
          {{ shortStatusText(document) }}
        </v-chip>

        <v-btn v-if="$vuetify.breakpoint.mdAndUp" text class="toolbar-action mr-1" @click="$emit('download-document', document)">
          <v-icon left small>mdi-download-outline</v-icon>
          Herunterladen
        </v-btn>

        <v-btn
          v-if="$vuetify.breakpoint.mdAndUp"
          icon
          class="touch-target toolbar-icon-action mr-1"
          aria-label="Dokument bearbeiten"
          @click="$emit('edit-document', document)">
          <v-icon size="21">mdi-pencil-outline</v-icon>
        </v-btn>

        <template
          v-if="userService.hasRight('d_fetchAllDocs') && document.approvalStatus === 'notSigned' && document.currentUserStatus !== 'accepted'">
          <v-btn v-if="$vuetify.breakpoint.smAndUp" color="primary" depressed class="primary-action mr-2" @click="confirmSignDialog = true">
            <v-icon left small>mdi-check-decagram-outline</v-icon>
            Visieren
          </v-btn>

          <v-btn
            v-else
            icon
            class="mobile-sign-action"
            aria-label="Dokument jetzt visieren"
            title="Dokument jetzt visieren"
            @click="confirmSignDialog = true">
            <v-icon size="21">mdi-check-decagram-outline</v-icon>
          </v-btn>
        </template>

        <v-menu v-if="$vuetify.breakpoint.smAndDown" offset-y left content-class="workspace-menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="touch-target" aria-label="Weitere Dokumentaktionen" v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dark dense class="workspace-menu-list">
            <v-list-item @click="$emit('download-document', document)">
              <v-list-item-icon><v-icon>mdi-download-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Herunterladen</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('edit-document', document)">
              <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Bearbeiten</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <v-card-text class="workspace-body pa-0 flex-grow-1">
        <div class="workspace-layout">
          <section class="preview-pane" aria-label="Dokumentvorschau">
            <div class="preview-shell">
              <div class="preview-toolbar">
                <div class="preview-toolbar-heading">
                  <div class="preview-toolbar-icon">
                    <v-icon size="18">mdi-eye-outline</v-icon>
                  </div>
                  <div>
                    <div class="preview-label">Vorschau</div>
                    <div class="preview-caption">Dokumentinhalt</div>
                  </div>
                </div>

                <div class="preview-toolbar-actions">
                  <div class="preview-state" :class="{ 'is-loading': previewLoading, 'is-muted': !previewLoading && !hasPreview }">
                    <span class="preview-state-dot"></span>
                    {{ previewStateText }}
                  </div>
                  <v-btn icon small class="preview-download" aria-label="Dokument herunterladen" @click="$emit('download-document', document)">
                    <v-icon size="19">mdi-download-outline</v-icon>
                  </v-btn>
                </div>
              </div>

              <div v-if="!isApp" class="preview-stage">
                <div v-if="previewLoading" class="preview-loading" role="status" aria-live="polite">
                  <div class="loading-document" aria-hidden="true">
                    <span class="loading-line loading-line-wide"></span>
                    <span class="loading-line"></span>
                    <span class="loading-line loading-line-short"></span>
                    <span class="loading-block"></span>
                    <span class="loading-line"></span>
                  </div>
                  <div class="loading-copy">
                    <v-progress-circular indeterminate color="primary" size="18" width="2"></v-progress-circular>
                    <span>Vorschau wird vorbereitet …</span>
                  </div>
                </div>

                <iframe v-else-if="previewBlobUrl" :src="previewBlobUrl" class="pdf-preview" title="Dokumentvorschau"></iframe>

                <div v-else-if="previewHtml" class="docx-scroll">
                  <article class="docx-preview" v-html="previewHtml"></article>
                </div>

                <div v-else class="preview-empty" role="status">
                  <div class="empty-icon">
                    <v-icon size="32">mdi-file-eye-outline</v-icon>
                  </div>
                  <div class="empty-title">Keine Vorschau verfügbar</div>
                  <div class="empty-copy">
                    Dieses Dateiformat kann möglicherweise nicht direkt angezeigt werden. Du kannst das Dokument weiterhin herunterladen.
                  </div>
                  <v-btn outlined color="primary" class="secondary-action mt-5" @click="$emit('download-document', document)">
                    <v-icon left>mdi-download-outline</v-icon>
                    Dokument laden
                  </v-btn>
                </div>
              </div>

              <div v-else class="preview-empty app-preview-message">
                <div class="empty-icon">
                  <v-icon size="32">mdi-cellphone-arrow-down</v-icon>
                </div>
                <div class="empty-title">Vorschau in der App nicht verfügbar</div>
                <div class="empty-copy">Lade das Dokument herunter, um es auf deinem Gerät zu öffnen.</div>
                <v-btn color="primary" depressed class="primary-action mt-5" @click="$emit('download-document', document)">
                  <v-icon left>mdi-download-outline</v-icon>
                  Herunterladen
                </v-btn>
              </div>
            </div>
          </section>

          <aside class="details-pane" aria-label="Dokumentdetails">
            <v-tabs v-model="localActiveTab" dark show-arrows center-active grow class="workspace-tabs flex-grow-0" slider-color="primary">
              <v-tab>
                <v-icon small>mdi-information-outline</v-icon>
                <span>Details</span>
              </v-tab>
              <v-tab>
                <v-icon small>mdi-comment-text-outline</v-icon>
                <span>Freigabe</span>
              </v-tab>
              <v-tab>
                <v-icon small>mdi-history</v-icon>
                <span>Verlauf</span>
              </v-tab>
              <v-tab>
                <v-icon small>mdi-account-group-outline</v-icon>
                <span>Personen</span>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="localActiveTab" dark class="workspace-tab-items flex-grow-1">
              <v-tab-item class="tab-panel">
                <div class="tab-scroll">
                  <div class="status-banner" :class="statusClass(document)">
                    <div class="status-banner-icon">
                      <v-icon :color="statusColor(document)">{{ statusIcon(document) }}</v-icon>
                    </div>
                    <div class="status-banner-copy">
                      <div class="status-label">Status</div>
                      <div class="status-value">{{ alertTextByStatus(document) }}</div>
                    </div>
                    <v-btn
                      v-if="document.approvalStatus === 'notSigned' && document.currentUserStatus !== 'accepted'"
                      text
                      color="primary"
                      class="status-link"
                      @click="localActiveTab = 1">
                      Prüfen
                    </v-btn>
                  </div>

                  <section class="detail-section">
                    <div class="section-heading">
                      <v-icon small>mdi-text-box-outline</v-icon>
                      <span>Allgemein</span>
                    </div>
                    <dl class="detail-list">
                      <div class="detail-row">
                        <dt>Titel</dt>
                        <dd>{{ document.title || "-" }}</dd>
                      </div>
                      <div class="detail-row">
                        <dt>Beschreibung</dt>
                        <dd class="preserve-lines">{{ document.description || "-" }}</dd>
                      </div>
                    </dl>
                  </section>

                  <section class="detail-section">
                    <div class="section-heading">
                      <v-icon small>mdi-receipt-text-outline</v-icon>
                      <span>Rechnung</span>
                    </div>
                    <dl class="detail-list">
                      <div class="detail-row">
                        <dt>Datum</dt>
                        <dd>{{ formatDate(document.date) }}</dd>
                      </div>
                      <div class="detail-row">
                        <dt>Rechnungssteller</dt>
                        <dd>{{ document.invoiceIssuer ? document.invoiceIssuer.title : "-" }}</dd>
                      </div>
                      <div class="detail-row">
                        <dt>Rechnungsnummer</dt>
                        <dd>{{ document.invoiceNumber || "-" }}</dd>
                      </div>
                      <div class="detail-row">
                        <dt>Betrag</dt>
                        <dd class="detail-emphasis">{{ document.amount ? document.amount : "-" }}</dd>
                      </div>
                    </dl>
                  </section>

                  <section class="detail-section">
                    <div class="section-heading">
                      <v-icon small>mdi-email-outline</v-icon>
                      <span>E-Mail</span>
                    </div>
                    <dl v-if="mailDetails" class="detail-list">
                      <div class="detail-row">
                        <dt>Datum</dt>
                        <dd>{{ formatDateTime(mailDetails.date) }}</dd>
                      </div>
                      <div class="detail-row">
                        <dt>Betreff</dt>
                        <dd>{{ mailDetails.subject || "-" }}</dd>
                      </div>
                      <div class="detail-row">
                        <dt>Von</dt>
                        <dd>{{ formatSenders(mailDetails.from) }}</dd>
                      </div>
                      <div class="detail-row detail-row-stacked">
                        <dt>Inhalt</dt>
                        <dd class="mail-body">{{ mailDetails.text || "Kein Text" }}</dd>
                      </div>
                    </dl>
                    <div v-else class="section-empty">
                      <v-icon small>mdi-email-off-outline</v-icon>
                      <span>Keine Mail-Daten verknüpft</span>
                    </div>
                  </section>
                </div>
              </v-tab-item>

              <v-tab-item class="tab-panel">
                <div class="decision-layout">
                  <div class="comment-stream">
                    <div v-if="document.comments && document.comments.length > 0">
                      <article v-for="(comment, index) in document.comments" :key="index" class="comment-card">
                        <div class="comment-meta">
                          <div class="comment-avatar">
                            {{ getUserInitials(comment.user) }}
                          </div>
                          <div class="comment-author">
                            <strong>{{ getUserName(comment.user) }}</strong>
                            <span>{{ formatDateTime(comment.createdAt) }}</span>
                          </div>
                          <v-chip v-if="comment.status" x-small outlined :color="comment.status === 'accepted' ? 'success' : 'error'">
                            {{ comment.status === "accepted" ? "Akzeptiert" : "Abgelehnt" }}
                          </v-chip>
                        </div>
                        <div class="comment-text">{{ comment.comment }}</div>
                      </article>
                    </div>
                    <div v-else class="section-empty large-empty">
                      <v-icon>mdi-comment-text-outline</v-icon>
                      <strong>Noch keine Kommentare</strong>
                      <span>Deine Entscheidung und Begründung erscheinen hier.</span>
                    </div>
                  </div>

                  <div class="decision-composer">
                    <label class="composer-label" for="document-comment">Kommentar</label>
                    <v-textarea
                      id="document-comment"
                      v-model="localComment"
                      placeholder="Kommentar oder Begründung schreiben …"
                      rows="2"
                      auto-grow
                      outlined
                      hide-details
                      class="workspace-input"></v-textarea>
                    <div class="decision-actions">
                      <v-btn outlined color="error" class="secondary-action" @click="decideAssignment('rejected')">
                        <v-icon left small>mdi-close</v-icon>
                        Ablehnen
                      </v-btn>
                      <v-btn depressed color="primary" class="primary-action" @click="decideAssignment('accepted')">
                        <v-icon left small>mdi-check</v-icon>
                        Akzeptieren
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item class="tab-panel">
                <div class="tab-scroll">
                  <v-timeline v-if="document.accessHistory && document.accessHistory.length > 0" align-top dense class="history-timeline">
                    <v-timeline-item v-for="(entry, index) in document.accessHistory" :key="index" small color="primary">
                      <div class="history-entry">
                        <div class="history-date">{{ formatDateTime(entry.grantedAt) }}</div>
                        <div class="history-title">
                          {{ entry.user.vorname ? entry.user.vorname + " " + entry.user.nachname : entry.user }}
                        </div>
                        <div class="history-copy">
                          {{ entry.action === "removed" ? "Zugriff entzogen von" : "Zugriff erhalten von" }}
                          {{ entry.grantedBy.vorname }} {{ entry.grantedBy.nachname }}
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                  <div v-else class="section-empty large-empty">
                    <v-icon>mdi-history</v-icon>
                    <strong>Noch kein Verlauf</strong>
                    <span>Zugriffsänderungen werden hier chronologisch angezeigt.</span>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item class="tab-panel">
                <div class="tab-scroll">
                  <section class="assignment-box">
                    <div class="section-heading">
                      <v-icon small>mdi-account-plus-outline</v-icon>
                      <span>Person hinzufügen</span>
                    </div>
                    <user-search
                      :value="newAssignedUser"
                      ignoreRights
                      instructor
                      :disabled="false"
                      :key="newAssignedUser"
                      label="Person suchen"
                      @update="updateNewAssignedUser"></user-search>
                    <v-btn block color="primary" depressed class="primary-action mt-3" :disabled="!newAssignedUser" @click="addAssignedUser">
                      <v-icon left small>mdi-plus</v-icon>
                      Hinzufügen
                    </v-btn>
                  </section>

                  <section class="detail-section">
                    <div class="section-heading">
                      <v-icon small>mdi-account-multiple-outline</v-icon>
                      <span>Zugewiesene Personen</span>
                    </div>
                    <v-list v-if="document.assignedUsers && document.assignedUsers.length > 0" dark color="transparent" class="assignment-list pa-0">
                      <v-list-item
                        v-for="(assignment, index) in document.assignedUsers"
                        :key="assignment.user && assignment.user._id ? assignment.user._id : index"
                        class="assignment-row">
                        <v-list-item-avatar class="assignment-avatar" size="38">
                          <span>{{ getUserInitials(assignment.user) }}</span>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ getUserName(assignment.user) }}</v-list-item-title>
                          <v-list-item-subtitle>Rechte: {{ assignment.rights }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn icon class="touch-target" color="error" aria-label="Zuweisung entfernen" @click="removeAssignedUser(index)">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <div v-else class="section-empty">
                      <v-icon small>mdi-account-off-outline</v-icon>
                      <span>Keine Personen zugewiesen</span>
                    </div>
                  </section>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </aside>
        </div>
      </v-card-text>

      <v-dialog :value="confirmSignDialog" max-width="440" @input="confirmSignDialog = $event">
        <v-card dark class="confirm-card">
          <div class="confirm-icon">
            <v-icon size="30" color="primary">mdi-check-decagram-outline</v-icon>
          </div>
          <v-card-title class="confirm-title">Dokument visieren?</v-card-title>
          <v-card-text class="confirm-copy"> Du bestätigst damit, dass du dieses Dokument geprüft hast. </v-card-text>
          <v-card-actions class="confirm-actions">
            <v-btn text class="secondary-action" @click="confirmSignDialog = false">Abbrechen</v-btn>
            <v-btn color="primary" depressed class="primary-action" @click="confirmSign"> Jetzt visieren </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import userSearch from "@/components/userSearch.vue"
import userService from "@/services/userService"

export default {
  name: "DocumentActionsDialog",
  components: { userSearch },
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    document: {
      type: Object,
      required: true,
    },
    previewBlobUrl: {
      type: String,
      default: null,
    },
    previewHtml: {
      type: String,
      default: null,
    },
    previewHeight: {
      type: Number,
      default: null,
    },
    isResizing: {
      type: Boolean,
      default: false,
    },
    mailDetails: {
      type: Object,
      default: null,
    },
    activeTab: {
      type: Number,
      default: 0,
    },
    newComment: {
      type: String,
      default: "",
    },
    newAssignedUser: {
      type: [String, Object],
      default: null,
    },
    newAssignedUserRights: {
      type: String,
      default: "manage",
    },
  },
  data() {
    return {
      localActiveTab: this.activeTab,
      localComment: this.newComment,
      confirmSignDialog: false,
      previewLoading: false,
      previewLoadingTimer: null,
      userService,
    }
  },
  watch: {
    activeTab(newVal) {
      this.localActiveTab = newVal
    },
    localActiveTab(newVal) {
      this.$emit("update:activeTab", newVal)
    },
    newComment(newVal) {
      if (newVal !== this.localComment) this.localComment = newVal
    },
    localComment(newVal) {
      this.$emit("update-new-comment", newVal)
    },
    dialog(newVal) {
      if (newVal) {
        this.startPreviewLoading()
      } else {
        this.confirmSignDialog = false
        this.stopPreviewLoading()
      }
    },
    document(newVal, oldVal) {
      if (this.dialog && newVal !== oldVal) this.startPreviewLoading()
    },
    previewBlobUrl(newVal) {
      if (newVal) this.finishPreviewLoading()
    },
    previewHtml(newVal, oldVal) {
      if (newVal || (oldVal && !newVal)) this.finishPreviewLoading()
    },
  },
  computed: {
    isApp() {
      return window.flutter_inappwebview || false
    },
    previewStateText() {
      if (this.isApp) return "Extern öffnen"
      if (this.previewLoading) return "Wird geladen"
      if (this.hasPreview) return "Bereit"
      return "Nicht verfügbar"
    },
    hasPreview() {
      return Boolean(this.previewBlobUrl || this.previewHtml)
    },
  },
  beforeDestroy() {
    this.clearPreviewLoadingTimer()
  },
  methods: {
    onDialogInput(value) {
      if (!value) this.$emit("close")
    },
    clearPreviewLoadingTimer() {
      if (!this.previewLoadingTimer) return
      window.clearTimeout(this.previewLoadingTimer)
      this.previewLoadingTimer = null
    },
    startPreviewLoading() {
      this.clearPreviewLoadingTimer()
      if (this.isApp) {
        this.previewLoading = false
        return
      }
      this.previewLoading = true
      this.previewLoadingTimer = window.setTimeout(() => {
        this.previewLoading = false
        this.previewLoadingTimer = null
      }, 2400)
    },
    finishPreviewLoading() {
      this.clearPreviewLoadingTimer()
      this.previewLoading = false
    },
    stopPreviewLoading() {
      this.clearPreviewLoadingTimer()
      this.previewLoading = false
    },
    statusIcon(doc) {
      if (doc.approvalStatus === "signed") return "mdi-check-circle"
      if (doc.approvalStatus === "declined") return "mdi-close-circle"
      if (doc.currentUserStatus === "accepted") return "mdi-check-circle-outline"
      return "mdi-clock-outline"
    },
    statusColor(doc) {
      if (doc.approvalStatus === "signed" || doc.currentUserStatus === "accepted") return "success"
      if (doc.approvalStatus === "declined") return "error"
      return "warning"
    },
    statusClass(doc) {
      if (doc.approvalStatus === "signed" || doc.currentUserStatus === "accepted") return "status-success"
      if (doc.approvalStatus === "declined") return "status-error"
      return "status-warning"
    },
    shortStatusText(doc) {
      if (doc.approvalStatus === "signed") return "Visiert"
      if (doc.approvalStatus === "declined") return "Abgelehnt"
      if (doc.currentUserStatus === "accepted") return "Von dir visiert"
      return "Offen"
    },
    alertTextByStatus(doc) {
      if (doc.approvalStatus === "signed") return "Dokument ist visiert"
      if (doc.approvalStatus === "declined") return "Dokument wurde abgelehnt"
      if (doc.currentUserStatus === "accepted") return "Von dir visiert – wartet auf weitere Personen"
      if (doc.approvalStatus === "notSigned") return "Dokument ist noch nicht visiert"
      return "Unbekannter Status"
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(date) {
      return date ? this.$moment(date).format("DD.MM.YY HH:mm") : "-"
    },
    formatSenders(senders) {
      if (Array.isArray(senders)) return senders.join(", ")
      return senders || "-"
    },
    getUserName(user) {
      if (!user) return "Unbekannte Person"
      if (typeof user === "string") return user
      const fullName = [user.vorname, user.nachname].filter(Boolean).join(" ")
      return fullName || "Unbekannte Person"
    },
    getUserInitials(user) {
      if (!user || typeof user === "string") return typeof user === "string" && user.length ? user.charAt(0).toUpperCase() : "?"
      return `${(user.vorname || "U").charAt(0)}${(user.nachname || "").charAt(0)}`.toUpperCase()
    },
    confirmSign() {
      this.confirmSignDialog = false
      const payload = { status: "accepted", comment: "" }
      if (!this.document.assignedUsers || this.document.assignedUsers.length === 0) {
        const currentUser = this.userService.getUser()
        if (currentUser && currentUser._id) payload.assignUser = currentUser._id
      }
      this.$emit("decide-assignment", payload)
    },
    decideAssignment(status) {
      if (status === "rejected" && (!this.localComment || this.localComment.length === 0)) {
        this.$toast.error("Gib bitte eine Begründung an")
        return
      }
      this.$emit("decide-assignment", { status, comment: this.localComment })
    },
    removeAssignedUser(index) {
      this.$emit("remove-assigned-user", index)
    },
    addAssignedUser() {
      this.$emit("add-assigned-user")
    },
    updateNewAssignedUser(value) {
      this.$emit("update-new-assigned-user", value)
    },
  },
}
</script>

<style scoped>
.document-workspace {
  --workspace-bg: rgba(28, 28, 29, 0.95);
  --workspace-surface: rgba(255, 255, 255, 0.035);
  --workspace-surface-raised: rgba(255, 255, 255, 0.055);
  --workspace-border: rgba(255, 255, 255, 0.1);
  --workspace-border-strong: rgba(255, 255, 255, 0.16);
  --workspace-text: #f4f4f5;
  --workspace-muted: #a3a3a8;
  --workspace-soft: #d4d4d8;
  --workspace-primary: #009640;
  --workspace-primary-soft: #55d185;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), var(--workspace-bg) !important;
  color: var(--workspace-text);
  overflow: hidden;
  height: min(92vh, 1120px) !important;
  min-height: min(620px, calc(100vh - 32px));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 26px !important;
  box-shadow: 0 42px 110px rgba(0, 0, 0, 0.58), 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.workspace-toolbar {
  min-height: 78px;
  padding: 0 16px;
  background: rgba(28, 28, 29, 0.78) !important;
  border-bottom: 1px solid var(--workspace-border);
  backdrop-filter: blur(18px);
}

.workspace-toolbar ::v-deep .v-toolbar__content {
  width: 100%;
  height: 78px !important;
}

.close-action {
  color: var(--workspace-soft) !important;
}

.close-action:hover {
  background: rgba(255, 255, 255, 0.075);
}

.document-mark {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  margin-left: 4px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.055);
  color: #d4d4d8;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.workspace-heading {
  min-width: 0;
  margin-left: 12px;
}

.workspace-eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  color: var(--workspace-muted);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  line-height: 1;
  text-transform: uppercase;
}

.eyebrow-divider {
  color: rgba(255, 255, 255, 0.28);
}

.workspace-title {
  max-width: min(38vw, 640px);
  color: var(--workspace-text);
  font-size: 1.03rem !important;
  font-weight: 750;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.touch-target {
  min-width: 44px !important;
  min-height: 44px !important;
}

.primary-action,
.secondary-action {
  min-height: 44px !important;
  border-radius: 13px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action:not(.v-btn--disabled) {
  color: #ffffff !important;
  background-color: #009640 !important;
  box-shadow: 0 8px 22px rgba(0, 150, 64, 0.16) !important;
}

.primary-action:not(.v-btn--disabled):hover,
.primary-action:not(.v-btn--disabled):focus-visible {
  background-color: #00aa49 !important;
}

.secondary-action.primary--text,
.status-link.primary--text {
  color: #55d185 !important;
}

.mobile-sign-action {
  flex: 0 0 44px;
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  margin-right: 4px !important;
  border: 1px solid rgba(0, 150, 64, 0.42);
  border-radius: 14px !important;
  color: #66d691 !important;
  background: rgba(0, 150, 64, 0.14) !important;
  box-shadow: none !important;
}

.mobile-sign-action:hover,
.mobile-sign-action:focus-visible {
  border-color: rgba(85, 209, 133, 0.68);
  background: rgba(0, 150, 64, 0.22) !important;
}

.status-chip {
  height: 32px !important;
  border-radius: 10px !important;
  font-weight: 700;
}

.toolbar-action {
  min-height: 42px !important;
  padding: 0 14px !important;
  border-radius: 12px !important;
  color: var(--workspace-soft) !important;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.toolbar-icon-action {
  color: var(--workspace-soft) !important;
}

.toolbar-action:hover,
.toolbar-icon-action:hover {
  background: rgba(255, 255, 255, 0.075);
  color: var(--workspace-text) !important;
}

.workspace-body {
  min-height: 0;
  overflow: hidden;
}

.workspace-layout {
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 0;
  grid-template-columns: minmax(0, 1fr) clamp(390px, 28vw, 460px);
}

.preview-pane {
  width: 100%;
  min-height: 0;
  padding: clamp(14px, 1.25vw, 22px);
  overflow: hidden;
  background: rgba(0, 0, 0, 0.12);
}

.preview-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--workspace-border);
  border-radius: 20px;
  background: rgba(7, 7, 8, 0.62);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
}

.preview-toolbar {
  display: flex;
  flex: 0 0 56px;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  padding: 0 12px 0 15px;
  border-bottom: 1px solid var(--workspace-border);
  background: rgba(255, 255, 255, 0.035);
}

.preview-toolbar-heading,
.preview-toolbar-actions {
  display: flex;
  align-items: center;
}

.preview-toolbar-heading {
  min-width: 0;
  gap: 10px;
}

.preview-toolbar-icon {
  display: grid;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: #d4d4d8;
}

.preview-label {
  color: var(--workspace-text);
  font-size: 0.78rem;
  font-weight: 750;
  line-height: 1.2;
}

.preview-caption {
  margin-top: 2px;
  color: var(--workspace-muted);
  font-size: 0.65rem;
  line-height: 1.2;
}

.preview-toolbar-actions {
  flex: 0 0 auto;
  gap: 8px;
}

.preview-state {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 64, 0.3);
  border-radius: 999px;
  background: rgba(0, 150, 64, 0.09);
  color: #70d99a;
  font-size: 0.66rem;
  font-weight: 700;
}

.preview-state.is-loading {
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.045);
  color: #c8c8cc;
}

.preview-state.is-muted {
  border-color: rgba(255, 255, 255, 0.11);
  background: rgba(255, 255, 255, 0.035);
  color: var(--workspace-muted);
}

.preview-state-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.11);
}

.preview-state.is-loading .preview-state-dot {
  animation: preview-pulse 1.3s ease-in-out infinite;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.07);
}

.preview-download {
  color: var(--workspace-soft) !important;
}

.preview-download:hover {
  background: rgba(255, 255, 255, 0.075);
}

.preview-stage {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  background: rgba(7, 7, 8, 0.76);
}

.app-preview-message {
  flex: 1;
}

.pdf-preview {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.docx-scroll {
  height: 100%;
  padding: clamp(18px, 2vw, 34px);
  overflow: auto;
  background: radial-gradient(circle at 50% 0%, #eeeeef, #d8d8da 72%);
}

.docx-preview {
  width: min(100%, 920px);
  min-height: 100%;
  margin: 0 auto;
  padding: clamp(28px, 5vw, 64px);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.2);
  color: #232325;
  font-family: Arial, sans-serif;
  line-height: 1.6;
}

.preview-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  padding: 32px;
  background: rgba(12, 12, 13, 0.97);
}

.loading-document {
  display: flex;
  width: min(240px, 56%);
  aspect-ratio: 0.72;
  padding: 34px 28px;
  flex-direction: column;
  gap: 12px;
  border-radius: 9px;
  background: rgba(226, 232, 240, 0.94);
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.3);
}

.loading-line,
.loading-block {
  display: block;
  border-radius: 5px;
  background: linear-gradient(90deg, #d4d4d8 24%, #eeeeef 48%, #d4d4d8 72%);
  background-size: 240% 100%;
  animation: preview-shimmer 1.6s linear infinite;
}

.loading-line {
  width: 78%;
  height: 8px;
}

.loading-line-wide {
  width: 100%;
  height: 12px;
  margin-bottom: 5px;
}

.loading-line-short {
  width: 56%;
}

.loading-block {
  width: 100%;
  height: 30%;
  margin: 8px 0;
}

.loading-copy {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--workspace-muted);
  font-size: 0.78rem;
  font-weight: 650;
}

@keyframes preview-shimmer {
  to {
    background-position: -240% 0;
  }
}

@keyframes preview-pulse {
  50% {
    opacity: 0.35;
    transform: scale(0.82);
  }
}

.docx-preview ::v-deep img {
  max-width: 100%;
  height: auto;
}

.docx-preview ::v-deep table {
  max-width: 100%;
  border-collapse: collapse;
}

.docx-preview ::v-deep td,
.docx-preview ::v-deep th {
  padding: 7px 9px;
  border: 1px solid #d4d4d8;
}

.preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px;
  text-align: center;
}

.empty-icon {
  display: grid;
  width: 66px;
  height: 66px;
  margin-bottom: 18px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.055);
  color: #d4d4d8;
}

.empty-title {
  color: var(--workspace-text);
  font-size: 1.05rem;
  font-weight: 750;
}

.empty-copy {
  max-width: 420px;
  margin-top: 8px;
  color: var(--workspace-muted);
  font-size: 0.88rem;
  line-height: 1.65;
}

.details-pane {
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid var(--workspace-border);
  background: rgba(255, 255, 255, 0.018);
}

.workspace-tabs {
  min-height: 58px;
  padding: 7px 8px 0;
  background: rgba(0, 0, 0, 0.12) !important;
  border-bottom: 1px solid var(--workspace-border);
}

.workspace-tabs ::v-deep .v-tabs-bar {
  height: 50px;
  background: transparent !important;
}

.workspace-tabs ::v-deep .v-slide-group__content {
  gap: 3px;
}

.workspace-tabs ::v-deep .v-tab {
  min-width: 0;
  min-height: 42px;
  gap: 6px;
  margin-bottom: 6px;
  padding: 0 7px;
  border-radius: 11px;
  color: var(--workspace-muted) !important;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  transition: color 0.2s ease, background 0.2s ease;
}

.workspace-tabs ::v-deep .v-tab--active {
  background: rgba(0, 150, 64, 0.12);
  color: #69d694 !important;
}

.workspace-tabs ::v-deep .v-tabs-slider-wrapper {
  height: 2px !important;
}

.workspace-tabs ::v-deep .v-tabs-slider {
  background-color: #009640 !important;
}

.workspace-tab-items {
  min-height: 0;
  background: transparent !important;
}

.workspace-tab-items ::v-deep .v-window__container,
.workspace-tab-items ::v-deep .v-window-item {
  height: 100%;
}

.tab-panel {
  height: 100%;
  background: transparent !important;
}

.tab-scroll,
.comment-stream {
  height: 100%;
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
  scrollbar-width: thin;
}

.status-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid var(--workspace-border);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.045);
}

.status-success {
  border-color: rgba(0, 150, 64, 0.3);
  background: rgba(0, 150, 64, 0.095);
}

.status-success ::v-deep .success--text,
.status-chip.success--text,
.confirm-icon ::v-deep .primary--text,
.loading-copy ::v-deep .primary--text {
  color: #55d185 !important;
}

.status-error {
  border-color: rgba(248, 113, 113, 0.24);
  background: rgba(239, 68, 68, 0.08);
}

.status-warning {
  border-color: rgba(251, 191, 36, 0.24);
  background: rgba(245, 158, 11, 0.08);
}

.status-banner-icon {
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}

.status-banner-copy {
  min-width: 0;
  flex: 1;
}

.status-label {
  color: var(--workspace-muted);
  font-size: 0.67rem;
  font-weight: 750;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.status-value {
  margin-top: 2px;
  color: var(--workspace-text);
  font-size: 0.8rem;
  font-weight: 650;
  line-height: 1.4;
}

.status-link {
  min-height: 36px;
  padding: 0 9px !important;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: none;
}

.detail-section,
.assignment-box {
  margin-bottom: 12px;
  padding: 14px;
  border: 1px solid var(--workspace-border);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.035);
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 11px;
  color: var(--workspace-soft);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.detail-list {
  margin: 0;
}

.detail-row {
  display: grid;
  grid-template-columns: minmax(104px, 34%) 1fr;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.detail-row:first-child {
  padding-top: 0;
  border-top: 0;
}

.detail-row:last-child {
  padding-bottom: 0;
}

.detail-row dt {
  color: var(--workspace-muted);
  font-size: 0.73rem;
}

.detail-row dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--workspace-text);
  font-size: 0.79rem;
  line-height: 1.45;
}

.detail-row-stacked {
  grid-template-columns: 1fr;
  gap: 8px;
}

.detail-emphasis {
  color: #eeeeef !important;
  font-size: 0.95rem !important;
  font-weight: 800;
}

.preserve-lines,
.mail-body {
  white-space: pre-wrap;
}

.mail-body {
  max-height: 190px;
  padding: 11px;
  overflow-y: auto;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: var(--workspace-soft) !important;
}

.section-empty {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px;
  border: 1px dashed var(--workspace-border-strong);
  border-radius: 12px;
  color: var(--workspace-muted);
  font-size: 0.77rem;
}

.large-empty {
  min-height: 240px;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.large-empty strong {
  color: var(--workspace-soft);
}

.large-empty span {
  max-width: 290px;
  line-height: 1.55;
}

.decision-layout {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
}

.comment-stream {
  flex: 1;
  min-height: 0;
}

.comment-card {
  margin-bottom: 12px;
  padding: 15px;
  border: 1px solid var(--workspace-border);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.045);
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.comment-avatar,
.assignment-avatar {
  display: grid;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.065);
  color: #e4e4e7;
  font-size: 0.72rem;
  font-weight: 800;
}

.comment-author {
  min-width: 0;
  flex: 1;
}

.comment-author strong,
.comment-author span {
  display: block;
}

.comment-author strong {
  color: var(--workspace-text);
  font-size: 0.8rem;
}

.comment-author span {
  margin-top: 2px;
  color: var(--workspace-muted);
  font-size: 0.68rem;
}

.comment-text {
  margin-top: 12px;
  color: var(--workspace-soft);
  font-size: 0.84rem;
  line-height: 1.6;
  white-space: pre-wrap;
}

.decision-composer {
  padding: 16px 20px calc(16px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--workspace-border);
  background: rgba(24, 24, 25, 0.94);
  box-shadow: 0 -16px 34px rgba(0, 0, 0, 0.18);
}

.composer-label {
  display: block;
  margin-bottom: 8px;
  color: var(--workspace-soft);
  font-size: 0.76rem;
  font-weight: 700;
}

.workspace-input ::v-deep .v-input__slot {
  min-height: 70px;
  border-radius: 15px !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.decision-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.history-timeline {
  padding-top: 4px;
}

.history-entry {
  padding: 2px 0 14px;
}

.history-date {
  color: var(--workspace-muted);
  font-size: 0.7rem;
}

.history-title {
  margin-top: 4px;
  color: var(--workspace-text);
  font-size: 0.86rem;
  font-weight: 750;
}

.history-copy {
  margin-top: 4px;
  color: var(--workspace-soft);
  font-size: 0.76rem;
  line-height: 1.5;
}

.assignment-box {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.assignment-list {
  overflow: hidden;
  border-radius: 14px;
}

.assignment-row {
  min-height: 62px;
  margin-bottom: 8px;
  border: 1px solid var(--workspace-border);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.13);
}

.assignment-avatar {
  margin-right: 12px !important;
}

.confirm-card {
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 22px !important;
  background: #262627 !important;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.45);
}

.confirm-icon {
  display: grid;
  width: 58px;
  height: 58px;
  margin-bottom: 12px;
  place-items: center;
  border-radius: 18px;
  background: rgba(0, 150, 64, 0.13);
}

.confirm-title {
  padding: 0 0 8px !important;
  color: #f4f4f5;
  font-size: 1.2rem !important;
  font-weight: 800;
}

.confirm-copy {
  padding: 0 !important;
  color: #a3a3a8 !important;
  line-height: 1.6;
}

.confirm-actions {
  justify-content: flex-end;
  gap: 8px;
  padding: 22px 0 0 !important;
}

.workspace-menu-list {
  min-width: 210px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 14px;
  background: #29292a !important;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
}

.workspace-menu-list .v-list-item {
  min-height: 44px;
  border-radius: 10px;
}

@media (max-width: 959px) {
  .workspace-toolbar {
    min-height: 68px;
    padding: 0 10px;
  }

  .workspace-toolbar ::v-deep .v-toolbar__content {
    height: 68px !important;
  }

  .workspace-title {
    max-width: 48vw;
    font-size: 0.9rem !important;
  }

  .workspace-body {
    overflow-y: auto;
  }

  .workspace-layout {
    height: auto;
    min-height: 100%;
    grid-template-columns: minmax(0, 1fr);
  }

  .preview-pane {
    height: clamp(340px, 48vh, 520px);
    padding: 12px;
  }

  .preview-shell {
    border-radius: 17px;
  }

  .details-pane {
    height: clamp(520px, 64vh, 720px);
    min-height: 520px;
    border-top: 1px solid var(--workspace-border);
    border-left: 0;
  }

  .workspace-tabs {
    min-height: 58px;
  }

  .workspace-tabs ::v-deep .v-tabs-bar {
    height: 58px;
  }
}

@media (max-width: 599px) {
  .document-workspace {
    height: 100% !important;
    min-height: 0;
    border: 0;
    border-radius: 0 !important;
  }

  .workspace-toolbar {
    min-height: 64px;
    padding: 0 6px;
  }

  .workspace-toolbar ::v-deep .v-toolbar__content {
    height: 64px !important;
  }

  .document-mark {
    display: none;
  }

  .workspace-heading {
    margin-left: 3px;
  }

  .workspace-eyebrow {
    display: none;
  }

  .workspace-title {
    max-width: 44vw;
    font-size: 0.86rem !important;
  }

  .primary-action {
    min-width: 44px !important;
    padding: 0 12px !important;
  }

  .preview-pane {
    height: clamp(290px, 42vh, 390px);
    min-height: 290px;
    padding: 8px;
  }

  .preview-shell {
    border-radius: 14px;
  }

  .preview-toolbar {
    flex-basis: 50px;
    padding: 0 7px 0 11px;
  }

  .preview-toolbar-icon,
  .preview-caption {
    display: none;
  }

  .preview-state {
    min-height: 26px;
    padding: 0 8px;
  }

  .loading-document {
    width: min(185px, 58%);
    padding: 25px 21px;
  }

  .docx-scroll {
    padding: 10px;
  }

  .docx-preview {
    padding: 24px 18px;
  }

  .details-pane {
    height: max(540px, 64vh);
    min-height: 540px;
  }

  .workspace-tabs ::v-deep .v-tab {
    min-width: 0;
    gap: 4px;
    padding: 0 4px;
    font-size: 0.65rem;
  }

  .tab-scroll,
  .comment-stream {
    padding: 14px;
  }

  .status-banner {
    align-items: flex-start;
  }

  .status-link {
    min-width: auto !important;
    padding: 0 8px !important;
  }

  .detail-section,
  .assignment-box {
    padding: 13px;
    border-radius: 14px;
  }

  .detail-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .decision-composer {
    padding: 13px 14px calc(13px + env(safe-area-inset-bottom));
  }

  .decision-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .decision-actions .v-btn {
    min-width: 0 !important;
    padding: 0 10px !important;
  }

  .confirm-card {
    margin: 12px;
    padding: 20px;
    border-radius: 20px !important;
  }
}
</style>

<style>
.document-workspace-dialog {
  width: min(96vw, 1880px) !important;
  max-width: 1880px !important;
  max-height: 92vh !important;
  margin: 24px !important;
  overflow: visible !important;
  border-radius: 26px;
}

@media (max-width: 959px) {
  .document-workspace-dialog {
    width: calc(100vw - 32px) !important;
    max-height: 94vh !important;
    margin: 16px !important;
  }
}

@media (max-width: 599px) {
  .document-workspace-dialog {
    width: 100vw !important;
    max-width: none !important;
    height: 100% !important;
    max-height: none !important;
    margin: 0 !important;
    border-radius: 0;
  }
}
</style>
